<template>
  <!--
    고객 한 명의 주소록(V6 — `GET /telInfo/{seq}/addresses`) — 보기와 최소 고치기(별칭·상세·라이더 요청·숨기기). 새 주소 더하기·
    건물 찾기는 없다(관리자 웹에 주소 검색 화면이 없다 — POS 몫). 창을 열 때 한 번 읽는다(액션이 있을 때만 — 부모가 그린다).
    합쳐 숨긴 주소는 「합쳐짐」이고 고치기 대신 남긴 주소로 안내한다 — 판정은 `addressRowsOf` 한 곳.
  -->
  <div class="address-book">
    <h4 class="address-book__title">{{ $t(T.addressBookTitle) }}</h4>

    <p v-if="loadError" class="address-book__error">{{ loadError }}</p>
    <p v-else-if="loaded && rows.length === 0" class="address-book__empty">{{ $t(T.addressEmpty) }}</p>
    <el-table v-else-if="rows.length" :data="rows" size="mini" border class="address-book__table">
      <el-table-column :label="$t(T.addressLabel)" width="90" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.label" size="mini">{{ scope.row.label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('주소')" min-width="280">
        <template slot-scope="scope">
          <div class="address-book__text" :class="{ 'is-hidden': scope.row.hidden }">{{ scope.row.text }}</div>
          <div v-if="scope.row.sub" class="address-book__sub">{{ scope.row.sub }}</div>
          <div v-if="scope.row.needsBuilding" class="address-book__sub is-warn">{{ $t(T.addressNeedsBuilding) }}</div>
          <div v-if="scope.row.riderText" class="address-book__sub">{{ scope.row.riderText }}</div>
          <el-tag v-if="scope.row.hiddenTag" size="mini" type="info">{{ $t(scope.row.hiddenTag) }}</el-tag>
          <el-tag v-if="scope.row.imported" size="mini" type="warning">{{ $t(T.addressImported) }}</el-tag>
          <div v-if="scope.row.mergedText" class="address-book__sub">{{ scope.row.mergedText }}</div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('주문')" width="160" prop="usageText"></el-table-column>
      <el-table-column v-if="canEdit" width="90" align="center">
        <template slot-scope="scope">
          <el-button v-if="scope.row.editable" size="mini" @click="openEdit(scope.$index)">{{
            $t(T.addressEdit)
          }}</el-button>
          <span v-else class="address-book__sub address-book__hint">{{ $t(T.addressMergedEdit) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      class="address-book__dialog"
      :title="$t(T.addressDialogTitle)"
      :visible.sync="editVisible"
      width="520px"
      append-to-body>
      <el-form v-if="form" label-width="110px" size="small" @submit.native.prevent>
        <el-form-item :label="$t(T.addressLabel)" :error="fieldErrors.label">
          <el-select v-model="form.label" class="address-book__select">
            <el-option v-for="option in labelOptions" :key="option.value" :label="option.label" :value="option.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(T.addressDetail)" :error="fieldErrors.addrDtl">
          <el-input v-model="form.addrDtl" :maxlength="limits.addrDtl" show-word-limit></el-input>
        </el-form-item>
        <!-- 라이더 요청은 매장 라이더 문구에서만 고른다 — 문구를 못 읽었으면 칸을 그리지 않는다(값은 그대로, 본문에도 없다) -->
        <el-form-item v-if="riderPhrases" :label="$t(T.addressRider)" :error="fieldErrors.riderMemo">
          <el-select v-model="form.riderMemo" multiple class="address-book__select">
            <el-option v-for="option in riderOptions" :key="option.value" :label="option.label" :value="option.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(T.addressHide)">
          <el-checkbox v-model="form.hidden">{{ $t(T.addressHideHelp) }}</el-checkbox>
        </el-form-item>
      </el-form>
      <p v-if="saveError" class="address-book__error">{{ saveError }}</p>
      <span slot="footer">
        <el-button size="small" @click="editVisible = false">{{ $t('닫기') }}</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="save">{{ $t('저장') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { httpErrorBody, httpErrorMessage, httpStatusOf, rejectionFieldsOf } from '@/plugins/http/policy'
import { permittedRead } from '@/plugins/http/permittedRead'
import { latestGuard } from '@/utils/latestGuard'
import {
  TEL_INFO_ACTION,
  TEL_INFO_LIMITS,
  TEL_INFO_TEXT,
  addressFormOf,
  addressLabelOptionsOf,
  addressPatchOf,
  addressRowsOf,
  phraseOptionsOf,
  telInfoAddressUrl,
  telInfoAddressesUrl,
} from '@/utils/telInfo'

/** 고치기 요청 — 실패는 창 안에서 말한다 */
const SAVE_CONFIG = Object.freeze({ showErrorMessage: false })

/** 칸 오류를 앉힐 자리 — 본문 키와 같다 */
const blankFieldErrors = () => ({ label: '', addrDtl: '', riderMemo: '' })

export default {
  name: 'TelInfoAddressBook',

  props: {
    /** 고객(`tbl_tel_info.seq`) */
    telInfoSeq: { type: [Number, String], required: true },
    /** 매장 라이더 문구 — `null` 이면 못 읽었다(라이더 요청 칸을 그리지 않는다) */
    riderPhrases: { type: Array, default: null },
    /** `TelInfoAddressUp` 이 있는가 */
    canEdit: { type: Boolean, default: false },
  },

  data() {
    return {
      T: TEL_INFO_TEXT,
      limits: TEL_INFO_LIMITS,
      /** 받은 주소(V6 `items`) — 고치기 창은 이것과 견줘 바뀐 칸만 보낸다 */
      items: [],
      loaded: false,
      loadError: '',
      guard: latestGuard(),
      editVisible: false,
      editing: null,
      form: null,
      fieldErrors: blankFieldErrors(),
      saveError: '',
      saving: false,
    }
  },

  computed: {
    rows() {
      return addressRowsOf(this.items)
    },
    labelOptions() {
      return addressLabelOptionsOf(this.editing && this.editing.label)
    },
    riderOptions() {
      return phraseOptionsOf(this.riderPhrases, this.form ? this.form.riderMemo : [])
    },
  },

  watch: {
    telInfoSeq: 'load',
  },

  created() {
    this.load()
  },

  beforeDestroy() {
    this.guard.close()
  },

  methods: {
    /** 주소록 읽기 — 액션 게이트를 지난다(없으면 부르지 않는다, 403 = 로그아웃) */
    async load() {
      const token = this.guard.begin()
      this.loadError = ''

      try {
        const read = await permittedRead({
          action: TEL_INFO_ACTION.ADDRESS_LIST,
          url: telInfoAddressesUrl(this.telInfoSeq),
          showLoading: false,
          showErrorMessage: false,
        })
        if (!this.guard.isLatest(token)) return

        this.items = read.allowed && read.data && Array.isArray(read.data.items) ? read.data.items : []
        this.loaded = read.allowed
        if (!read.allowed) this.loadError = this.$t(this.T.addressLoadFailed)
      } catch (error) {
        if (!this.guard.isLatest(token)) return
        this.items = []
        this.loadError = httpErrorMessage(error, this.$t(this.T.addressLoadFailed))
      }
    },

    openEdit(index) {
      this.editing = this.items[index]
      this.form = addressFormOf(this.editing)
      this.fieldErrors = blankFieldErrors()
      this.saveError = ''
      this.editVisible = true
    },

    /**
     * 바뀐 칸만 보낸다(V6 — 키 없음 = 안 바꿈, `null` = 비움). 같은 고객의 다른 주소와 겹치면 서버가 합치고 이 주소를 숨긴다
     * (`mergedInto`) — 그때는 그렇게 말한다. 끝나면 주소록을 다시 읽는다.
     */
    async save() {
      const patch = addressPatchOf(addressFormOf(this.editing), this.form)
      if (Object.keys(patch).length === 0) {
        this.editVisible = false
        return
      }

      this.saving = true
      this.saveError = ''
      this.fieldErrors = blankFieldErrors()

      try {
        const response = await this.$axios.post(telInfoAddressUrl(this.editing.addressId), patch, SAVE_CONFIG)
        const merged = response && response.data && response.data.mergedInto != null

        this.$message({
          type: merged ? 'info' : 'success',
          message: this.$t(merged ? this.T.addressMerged : this.T.addressSaved),
        })
        this.editVisible = false
        this.load()
      } catch (error) {
        if (httpStatusOf(error) === 404) {
          // 그 사이 사라진 주소(합쳐졌거나 범위 밖) — 주소록을 다시 읽어 지금 모양을 보인다
          this.editVisible = false
          this.load()
          return
        }
        const fields = rejectionFieldsOf(httpErrorBody(error))
        const placed = Object.keys(blankFieldErrors()).filter((key) => fields[key])

        this.fieldErrors = Object.assign(blankFieldErrors(), ...placed.map((key) => ({ [key]: fields[key] })))
        if (placed.length === 0) this.saveError = httpErrorMessage(error, this.$t(this.T.addressSaveFailed))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.address-book__title {
  margin: 8px 0;
  font-size: 14px;
}
.address-book__text.is-hidden {
  color: #909399;
  text-decoration: line-through;
}
.address-book__sub {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
.address-book__sub.is-warn {
  color: #e6a23c;
}
/* 좁은 고치기 칸의 안내 — 낱말 가운데서 줄을 바꾸지 않는다(표 칸의 기본은 `break-all`) */
.address-book__hint {
  word-break: keep-all;
}
.address-book__empty {
  color: #909399;
  font-size: 13px;
}
.address-book__error {
  color: #f56c6c;
  font-size: 12px;
}
.address-book__select {
  width: 100%;
}
</style>
